/*
tslint:disable:import-spacing no-inferrable-types
variable-name align no-trailing-whitespace component-class-suffix
no-string-literal component-selector
*/

import 'ag-grid-enterprise';


import {Track} from '@/models/scenario/track';

import * as $ from 'jquery';

import 'select2';
import {OptGroupData, OptionData, SearchOptions} from 'select2';
import {TrackIngress} from "@/helpers/track.ingress";
import KeyUpEvent = JQuery.KeyUpEvent;
import KeyDownEvent = JQuery.KeyDownEvent;
import {ICellEditorComp, ICellEditorParams} from "ag-grid-community";

/**
 *
 * @class DiversionTrackEditor
 *
 * @brief dynamic dropdown list for picking which track to use at entry/exit of the diversion.
 *
 * We use Select2: https://select2.org  for the "combo box".
 *
 * @note The select2 @types/select2 (definitely typed stuff), includes "requirejs" which is
 * not going to be compatible with Angular.  Just edit its index.d.ts file and remove the
 * requirejs stuff after you add it to your npm repo.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2020
 *
 */
export class DiversionTrackEditor implements ICellEditorComp {

  /** agGrid DOM element for display */
  public gui: HTMLElement    = null;

  /** the location where we are picking ingress/egress */
  public value: TrackIngress = null;

  /** the selectable values (decision points) */
  public values: Track[]     = [];

  /** the callback to tell agGrid we're done editing */
  private editingDone: (suppressNavigateAfterEdit?: boolean) => void = null;

  private clickListenter: (e: MouseEvent) => void = null;

  /** set up the custom editor (happens on every edit attempt) */
  public init(params: ICellEditorParams): void {

    const te         = this;
    te.editingDone = params.stopEditing;

    te.value       = null;
    te.values      = [];

    te.gui          = document.createElement('div');
    te.gui.tabIndex = 0;

    /* pre-select value if there is an existing decision point selected already */

    if ( !(params.value instanceof TrackIngress)) {

      return;
    }

    te.value  = params.value;
    te.values = params.value.location.diversionTracks();

    /* set up the Select2 drop down ... */

    if (!te.values || (te.values.length === 0)) {

      /* nothing to do, we don't have any work blocks */

      return ;
    }

    /* if there was only one track...then use that */

    if (te.values.length === 1) {

      te.value = new TrackIngress(te.value.location, te.values[0]);
    }

    const data: any[] = [];

    /* walk the tracks, generating a select option for each */

    for (const t of te.values) {

      /* build this option ... */

      const name: string = Track.typeToString(t._type) + t._track;

      /* is this the current value? */

      let selected: boolean = false;

      if (te.value.track !== null) {

        selected = t._id === te.value.track._id ;
      }

      /* add the option */

      data.push({
        id: `track-${t._id}`,
        text: `(${t._id}) ${name}`,
        name,
        t,
        selected
      });

    }

    /* insert new HTML for our custom select ... */

    const html = `<select class="diversion-track-select"><option></option></select>`;
    $(te.gui).html(html);

    /* now create a Select2 version of it, that supports both selection and type ahead ... */

    const el: JQuery = $(te.gui).find('select.diversion-track-select');

    $(el).on('select2:close', () => {

      $(el).trigger('focus');

    });

    $(el).on("select2:closed", function() {

      $(el).select2("open");
    });

    $(el).on('select2:select', (e) => {

      const datum: any    = e.params.data;

      /* update the current value */

      te.value = new TrackIngress(te.value.location, datum.t);

      te.editingDone(true);
    });

    $(el).select2({

      placeholder:   `Select...`,

      allowClear:    false,
      closeOnSelect: true,
      width:         '200px',

      data,

      /* our type-ahead matching function */
      matcher: (args: SearchOptions, datum: OptGroupData | OptionData): any => {

        /* do we have something to match? */

        if ((args?.term ?? '').trim() === '') {

          return datum;
        }

        // @ts-ignore
        const name: string = datum?.name;

        if (!name) {

          /* likely the placeholder option */

          return null;
        }

        if (name.toUpperCase().indexOf(args.term.toUpperCase()) === 0) {

          return datum;
        }

        return null;
      }

    });

    $(el).on("select2:open", function() {

      /*
       * once the dropdown is open, make sure the selection area has the focus so if you press ENTER
       * we can finish editing.
       *
       */

      const selBox: JQuery = $('input.select2-search__field');

      $(selBox).trigger('focus');

      $(selBox).css('width', 'auto');
      $(selBox).val('');

      if ((params.eventKey ?? '').length === 1) {

        $(selBox).val(params.eventKey);
      }

    });

    setTimeout(() => {

      $(el).select2('open');

      $(te.gui).on('keyup', (e: KeyUpEvent) => {

        if (e.key !== 'Tab') {

          return ;
        }

        te.editingDone(true);

      });

      $(te.gui).on('keydown', (e: KeyDownEvent) => {

        if ((e.key !== 'Tab') && (e.key !== 'Enter')) {

          return ;
        }

        te.editingDone(true);

        e.preventDefault();
        e.stopPropagation();
      });

    }, 50);

    /*
     * watch for clicks outside the editor, implies closing the editor; the editor popups
     * are modal, there should only be one at a time.
     *
     */

    te.clickListenter = (e: MouseEvent) => {

      let target = e.target;

      do {

        if ($(target).hasClass(`select2-container`)) {

          /* it's ok, somewhere inside */

          return;
        }

        if ($(target).hasClass('select2-selection__choice')) {

          /* it's ok, somewhere inside */

          return;
        }

        if (target === te.gui) {

          /* it's ok, somewhere inside */

          return;
        }

        // @ts-ignore
        target = target.parentNode;

      } while (target);

      /* click is somewhere outside, auto-close */

      document.removeEventListener('click', te.clickListenter);

      te.editingDone(true);
    };

    document.addEventListener('click', te.clickListenter);
  }

  /** focus on local editing control (after it's attached to agGrid) */
  public afterGuiAttached(): void {

  }

  /* Return the DOM element of your editor, this is what the grid puts into the DOM */
  public getGui(): HTMLElement {

    return this.gui;
  }

  /* Should return the final value to the grid, the result of the editing */
  public getValue(): TrackIngress {

    return this.value;
  }

  /** do any clean up */
  destroy(): void {

    /* clear out our loss of focus listener */

    document.removeEventListener('click', this.clickListenter);

    /* deep six the select drop-down */

    const el: JQuery = $(this.gui).find('select.diversion-track-select');

    $(el).select2('destroy');

    /* remove the DOM we inserted */

    $(this.gui).remove();
  }

  /** return true to be placed in a popup */
  public isPopup(): boolean {

    return true;
  }

  /** return the positioning, either 'over' or 'under' (only for popups) */
  getPopupPosition(): 'over' | 'under' | undefined {

    return 'over';
  }

  /** return true to disallow editing before it even starts */
  isCancelBeforeStart(): boolean {

    /* if there are no decision points...nothing we can do */

    return this.values.length === 0;
  }

  /** return true to ignore editing change */
  isCancelAfterEnd(): boolean {

    return false;
  }

  /** on tabbing into the cell */
  public focusIn(): boolean {

    return true;
  }

  /** on tabbing out of the cell */
  public focusOut(): boolean {

    return true;
  }
}
